import React from 'react';

import {
  TRANSACTION_BANK_FIELDS,
  TRANSACTION_EXPENSE_FIELDS,
} from './constants';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  TableCell,
} from '@material-ui/core';
import { Layout } from '../global/Table/TablePageHelpers/LayoutObject';
import TablePage from '../global/Table/TablePage';
import TwoStepDelete from '../global/subcomponents/twoStepDelete/TwoStepDelete';
import { Tooltip } from '@mui/material';
import CommentIcon from '@mui/icons-material/Comment';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import AuthUtil from '../../redux/AuthUtil';
import { TRANSACTION } from '../global/Server/endpoints';
import './Transaction.css';

class TransactionTable extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      deleteId: null,
      open: false,
      comment: '',
      // Photo viewer for "EXTRA MATCH / LUCKY" transactions (mirrors Ticket Out).
      // The photo is fetched on demand from /transaction/previewPhoto and shown
      // in a dialog, so the list response stays lightweight.
      photoOpen: false,
      photoSrc: null,
      photoLoading: false,
      isMobile: window.matchMedia('(max-width: 800px)').matches,
    };
  }

  componentWillUnmount() {
    if (this.state.photoSrc) {
      URL.revokeObjectURL(this.state.photoSrc);
    }
  }

  getTableHeadersExpense = () => {
    let headers = [];
    TRANSACTION_EXPENSE_FIELDS.forEach((headCell) => {
      headers.push(
        Layout.columnHead(headCell.label, headCell.id, headCell.align)
      );
    });
    headers.push(Layout.columnHead(null, null));
    return headers;
  };

  getTableHeadersBank = () => {
    let headers = [];
    TRANSACTION_BANK_FIELDS.forEach((headCell) => {
      headers.push(
        Layout.columnHead(headCell.label, headCell.id, headCell.align)
      );
    });
    headers.push(Layout.columnHead(null, null));
    return headers;
  };

  handleClickOpen = (commentText) => {
    this.setState({
      open: true,
      comment: commentText,
    });
  };

  handleClose = () => {
    this.setState({ open: false });
  };

  // Renders the transaction timestamp as two lines — date on top, time below —
  // for a cleaner column. dateTime arrives as "yyyy-MM-dd hh:mm a" (see
  // DateUtil.formatDateWithTime), so the date is everything before the first
  // space and the time is the remainder. Both lines inherit the cell's font
  // (same black text/size as the Amount column) — no lighter/smaller styling.
  renderDateTime = (dateTime) => {
    if (!dateTime) return null;
    const idx = dateTime.indexOf(' ');
    const datePart = idx === -1 ? dateTime : dateTime.slice(0, idx);
    const timePart = idx === -1 ? '' : dateTime.slice(idx + 1);
    return (
      <>
        <div>{datePart}</div>
        {timePart && <div>{timePart}</div>}
      </>
    );
  };

  // Description with the linked person in brackets, matching the shift report
  // (e.g. "EXTRA MATCH / LUCKY (Hasnain Ali)"). The name is resolved server-side
  // (nameById, keyed by transactionId) so the same customer/employee rule the
  // report uses is applied here without a client-side customer lookup.
  descriptionWithName = (row) => {
    const who = this.props.nameById?.[row?.transactionId];
    return who ? `${row?.description} (${who})` : row?.description;
  };

  // Fetch a transaction's stored photo on demand and show it in a dialog.
  // Same contract as TicketOut.onViewPhoto: /transaction/previewPhoto returns
  // the raw bytes under `previewedFile`, which we wrap in a Blob object URL.
  handleViewPhoto = (transactionId) => {
    if (!transactionId) return;
    this.setState({ photoOpen: true, photoLoading: true, photoSrc: null });
    AuthUtil.postRequest(TRANSACTION.PREVIEW_PHOTO, { transactionId })
      .then((res) => {
        if (res.data && res.data.success && res.data.previewedFile) {
          const blob = new Blob([new Uint8Array(res.data.previewedFile)], {
            type: 'image/jpeg',
          });
          this.setState({
            photoSrc: URL.createObjectURL(blob),
            photoLoading: false,
          });
        } else {
          this.setState({ photoLoading: false });
        }
      })
      .catch(() => this.setState({ photoLoading: false }));
  };

  handleClosePhoto = () => {
    if (this.state.photoSrc) {
      URL.revokeObjectURL(this.state.photoSrc);
    }
    this.setState({ photoOpen: false, photoSrc: null, photoLoading: false });
  };

  tableRowExpense = (expense) => {
    return (
      <React.Fragment>
        <TableCell className="table-cell-left">
          {this.descriptionWithName(expense)}
        </TableCell>
        <TableCell className="table-cell-left">{expense?.amount} </TableCell>
        <TableCell
          className="table-cell-left"
          style={{ whiteSpace: 'nowrap' }}
        >
          {this.renderDateTime(expense?.dateTime)}
        </TableCell>
        <TableCell className="table-cell-left">
          {expense?.comments && (
            <Tooltip title={'Show Comment'}>
              <IconButton
                onClick={() => this.handleClickOpen(expense.comments)}
              >
                <CommentIcon fontSize="medium" />
              </IconButton>
            </Tooltip>
          )}
          {expense?.photoPath && (
            <Tooltip title={'View Photo'}>
              <IconButton
                onClick={() => this.handleViewPhoto(expense.transactionId)}
              >
                <PhotoCameraIcon fontSize="medium" />
              </IconButton>
            </Tooltip>
          )}
        </TableCell>

        {this.isDeleteAllowed(expense) ? (
          <TwoStepDelete
            rowId={expense?.transactionId}
            handleDelete={(rowIdToDelete) =>
              this.props.deleteTransaction({
                transactionId: expense?.transactionId,
              })
            }
            deleteId={this.state.deleteId}
            setDeleteId={(newDeleteId) =>
              this.setState({ deleteId: newDeleteId })
            }
          />
        ) : (
          <TableCell />
        )}
      </React.Fragment>
    );
  };

  tableRowBank = (bank) => {
    return (
      <React.Fragment>
        <TableCell className="table-cell-left">
          {this.descriptionWithName(bank)}
        </TableCell>
        <TableCell className="table-cell-left">{bank?.amount} </TableCell>
        <TableCell
          className="table-cell-left"
          style={{ whiteSpace: 'nowrap' }}
        >
          {this.renderDateTime(bank?.dateTime)}
        </TableCell>
        <TableCell className="table-cell-left">
          {bank?.comments && (
            <Tooltip title={'Comments'}>
              <IconButton onClick={() => this.handleClickOpen(bank.comments)}>
                <CommentIcon fontSize="medium" />
              </IconButton>
            </Tooltip>
          )}
          {bank?.photoPath && (
            <Tooltip title={'View Photo'}>
              <IconButton onClick={() => this.handleViewPhoto(bank.transactionId)}>
                <PhotoCameraIcon fontSize="medium" />
              </IconButton>
            </Tooltip>
          )}
        </TableCell>
        {this.isDeleteAllowed(bank) ? (
          <TwoStepDelete
            rowId={bank?.transactionId}
            handleDelete={(rowIdToDelete) =>
              this.props.deleteTransaction({
                transactionId: bank?.transactionId,
              })
            }
            deleteId={this.state.deleteId}
            setDeleteId={(newDeleteId) =>
              this.setState({ deleteId: newDeleteId })
            }
          />
        ) : (
          <TableCell />
        )}
      </React.Fragment>
    );
  };

  // Only the sender of a cash-left / transfer row can remove it.
  // Receiver-side rows ("CASH LEFT FROM …" / "TRANSFER MONEY FROM …")
  // are hidden from the receiver entirely — they have to ask the sender
  // to fix it. Other transaction types follow existing behavior.
  // Admins keep their override (matches the backend escape hatch).
  isDeleteAllowed = (row) => {
    const hasAdminRole = this.props.user?.authority?.includes('ROLE_ADMIN');
    if (hasAdminRole) return true;

    const desc = row?.description || '';
    const isReceiverSide =
      desc.startsWith('CASH LEFT FROM ') ||
      desc.startsWith('TRANSFER MONEY FROM ');
    if (isReceiverSide) return false;

    const isSenderSide =
      desc.startsWith('CASH LEFT TO ') || desc.startsWith('TRANSFER MONEY TO ');
    if (isSenderSide) {
      return row?.userId === this.props.user?.userId;
    }

    return true;
  };

  render() {
    const { expenseList, bankList } = this.props;
    const { open, comment, isMobile } = this.state;
    let totals = { expensesTotal: 0, bankTotal: 0 };

    if (Array.isArray(expenseList) && expenseList.length > 0) {
      totals.expensesTotal = expenseList.reduce(
        (total, expense) => total + parseFloat(expense.amount),
        0
      );
    }

    if (Array.isArray(bankList) && bankList.length > 0) {
      totals.bankTotal = bankList.reduce(
        (total, bank) => total + parseFloat(bank.amount),
        0
      );
    }

    return (
      <>
        <Dialog open={open} onClose={this.handleClose}>
          <DialogTitle>Comment</DialogTitle>
          <DialogContent style={{ width: '400px', height: '100px' }}>
            <Divider style={{ marginBottom: '16px', marginTop: '-8px' }} />
            <p>{comment}</p>
          </DialogContent>
          <DialogActions>
            <Button
              style={{ marginRight: '24px', marginBottom: '16px' }}
              variant="contained"
              color="primary"
              onClick={this.handleClose}
            >
              Close
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={this.state.photoOpen}
          onClose={this.handleClosePhoto}
          maxWidth="md"
        >
          <DialogTitle>Transaction Photo</DialogTitle>
          <DialogContent>
            <Divider style={{ marginBottom: '16px', marginTop: '-8px' }} />
            {this.state.photoLoading && <p>Loading photo…</p>}
            {!this.state.photoLoading && this.state.photoSrc && (
              <img
                src={this.state.photoSrc}
                alt="Transaction"
                style={{ maxWidth: '80vw', maxHeight: '75vh', display: 'block' }}
              />
            )}
            {!this.state.photoLoading && !this.state.photoSrc && (
              <p>Photo not available.</p>
            )}
          </DialogContent>
          <DialogActions>
            <Button
              style={{ marginRight: '24px', marginBottom: '16px' }}
              variant="contained"
              color="primary"
              onClick={this.handleClosePhoto}
            >
              Close
            </Button>
          </DialogActions>
        </Dialog>

        <div
          className="rowSpecSuppliers"
          style={{
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            width: '100%',
          }}
        >
          {/*<div className="rowSpecSuppliers">*/}
          {/*//this is for Bank*/}
          <div
            style={{
              width: isMobile ? '100%' : '50%',
              marginBottom: isMobile ? '24px' : '0',
            }}
          >
            <div
              style={{
                backgroundColor: '#6c93e9',
                color: 'black',
                padding: '16px',
                textAlign: 'center',
                fontWeight: 'bold',
                marginTop: '16px',
              }}
            >
              Bank Total: ({totals.bankTotal})
            </div>
            <div style={{}}>
              <TablePage
                tableName="bankTable"
                tableRows={bankList}
                tableColumnHeads={this.getTableHeadersBank()}
                tableRow={this.tableRowBank}
                paginationText="Results"
                defaultSortBy="dateTime"
              />
            </div>
          </div>

          {/*//this is for Expense*/}
          <div
            style={{
              width: isMobile ? '100%' : '50%',
              marginBottom: isMobile ? '16px' : '0px',
            }}
          >
            <div
              style={{
                backgroundColor: '#f4cc06',
                color: 'black',
                padding: '16px',
                textAlign: 'center',
                fontWeight: 'bold',
                marginTop: '16px',
              }}
            >
              Expenses Total: ({totals.expensesTotal})
            </div>
            <div
              style={{
                marginLeft: '-1px',
                borderLeft: '1px solid rgba(224, 224, 224, 1)',
              }}
            >
              <TablePage
                tableName="expenseTable"
                tableRows={expenseList}
                tableColumnHeads={this.getTableHeadersExpense()}
                tableRow={this.tableRowExpense}
                paginationText="Results"
                defaultSortBy="dateTime"
              />
            </div>
          </div>
        </div>
      </>
    );
  }
}

export default TransactionTable;
